/**
 * SliderStyles - Styled components for the Slider component.
 *
 * ARCHITECTURE:
 * - Provides styled components for slider container and input.
 * - Includes cross-browser slider styling for webkit and moz.
 * - Integrates with the existing theme system.
 */

import styled from '@emotion/styled'
import theme from '../../../styles/theme'
import { mix } from 'polished'

// Type definitions for styled component props
interface SliderContainerProps {
  trackWidth: number
  disabled: boolean
}

interface SliderTrackProps {
  isHovered: boolean
  trackWidth: number
  disabled: boolean
}

interface MiddleIndicatorProps {
  disabled: boolean
}

interface StyledSliderProps {
  isHovered: boolean
}

// Slider color variables for consistency
const sliderColors = {
  normal: mix(0.95, theme.colors.secondary, 'black'),
  hover: mix(0.9, theme.colors.secondary, 'black'),
  thumbNormal: mix(0.7, theme.colors.secondary, '#333333'),
  thumbHover: mix(0.5, theme.colors.secondary, '#333333'),
  thumbMozNormal: mix(0.7, theme.colors.secondary, 'grey'),
  thumbMozHover: mix(0.5, theme.colors.secondary, 'grey'),
}

export const SliderContainer = styled.div<SliderContainerProps>`
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 100%;
  position: relative;
  padding: 0 1rem;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 20px;
    background: ${mix(0.9, theme.colors.secondary, 'black')};
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    opacity: ${(props) => (props.disabled ? 0 : 1)};
    transition: opacity 0.2s ease;
  }

  &::before {
    left: calc(1rem + ${(props) => props.trackWidth}px + 10px);
  }

  &::after {
    right: calc(1rem + ${(props) => props.trackWidth}px + 10px);
  }
`

export const SliderTrackBefore = styled.div<SliderTrackProps>`
  width: ${(props) => props.trackWidth}px;
  height: 0.5rem;
  background: ${(props) =>
    props.isHovered ? sliderColors.hover : sliderColors.normal};
  opacity: ${(props) => (props.disabled ? 0 : 1)};
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;
`

export const SliderTrackAfter = styled.div<SliderTrackProps>`
  width: ${(props) => props.trackWidth}px;
  height: 0.5rem;
  background: ${(props) =>
    props.isHovered ? sliderColors.hover : sliderColors.normal};
  opacity: ${(props) => (props.disabled ? 0 : 1)};
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;
`

export const MiddleIndicator = styled.div<MiddleIndicatorProps>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 22px;
  background: ${mix(0.5, theme.colors.secondary, 'black')};
  z-index: 1;
  pointer-events: none;
  opacity: ${(props) => (props.disabled ? 0 : 1)};
  transition: opacity 0.2s ease;
`

export const StyledSlider = styled.input<StyledSliderProps>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 0.5rem;
  background-color: ${(props) =>
    props.isHovered ? sliderColors.hover : sliderColors.normal};
  outline: none;
  transition: background-color 0.2s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: ${theme.colors.backgroundDark};
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: ${(props) =>
      props.isHovered ? sliderColors.thumbHover : sliderColors.thumbNormal};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 2;
    position: relative;
    transition: background-color 0.2s ease;
  }

  &:disabled::-webkit-slider-thumb {
    background-color: ${theme.colors.veryVeryVeryFadedText};
    cursor: not-allowed;
    z-index: 2;
    position: relative;
  }

  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: ${(props) =>
      props.isHovered
        ? sliderColors.thumbMozHover
        : sliderColors.thumbMozNormal};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 2;
    position: relative;
    border: none;
    transition: background-color 0.2s ease;
    opacity: 1;
  }

  &:disabled::-moz-range-thumb {
    background: ${theme.colors.veryVeryVeryFadedText};
    cursor: not-allowed;
    z-index: 2;
    position: relative;
    border: none;
  }
`
